import React, { useState, useRef, useEffect } from 'react';
import { Camera, CameraOff, RefreshCw, Check, Upload } from 'lucide-react';

interface SelfieCaptureProps {
  onCapture: (file: File) => void;
  existingImage?: string;
  width?: number;
  height?: number;
  className?: string;
}

export default function SelfieCapture({
  onCapture,
  existingImage,
  width = 400,
  height = 400,
  className = ''
}: SelfieCaptureProps) {
  const [isStreaming, setIsStreaming] = useState(false);
  const [showVideoInterface, setShowVideoInterface] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(existingImage || null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Start camera
  const startCamera = async () => {
    console.log('startCamera function called!');
    console.log('videoRef.current:', videoRef.current);
    setError(null);
    setPermissionDenied(false);

    // First show the video interface so the video element renders
    setShowVideoInterface(true);

    // Small delay to ensure the video element is rendered
    setTimeout(async () => {
      console.log('After delay - videoRef.current:', videoRef.current);

      try {
        const constraints = {
          video: {
            facingMode: facingMode,
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        };
        console.log('Requesting media with constraints:', constraints);

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        console.log('Got stream:', stream);
        streamRef.current = stream;

        if (videoRef.current) {
          console.log('Setting video srcObject');
          videoRef.current.srcObject = stream;

          // Wait for video to be ready to play before showing the overlay
          videoRef.current.onloadedmetadata = () => {
            console.log('Video metadata loaded');
            videoRef.current?.play()
              .then(() => {
                console.log('Video is playing');
                setIsStreaming(true);
              })
              .catch((err) => {
                console.error('Error playing video:', err);
                setError('Unable to start video stream. Please try again.');
              });
          };

          // Also listen for canplay event as a fallback
          videoRef.current.oncanplay = () => {
            console.log('Video can play');
            if (!isStreaming) {
              setIsStreaming(true);
            }
          };

          // Set a timeout to ensure the overlay appears even if events don't fire
          setTimeout(() => {
            console.log('Timeout check - videoRef.current:', videoRef.current);
            console.log('Timeout check - readyState:', videoRef.current?.readyState);
            console.log('Timeout check - isStreaming:', isStreaming);
            if (videoRef.current && videoRef.current.readyState >= 2 && !isStreaming) {
              console.log('Forcing streaming state due to timeout');
              setIsStreaming(true);
            }
          }, 1000);
        } else {
          console.error('videoRef.current is still null after delay!');
          setError('Unable to initialize camera. Please try again.');
          setShowVideoInterface(false);
        }
      } catch (err: any) {
        console.error('Error accessing camera:', err);
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setPermissionDenied(true);
          setError('Camera access denied. Please allow camera access to take a selfie.');
        } else {
          setError('Unable to access camera. Please check your permissions.');
        }
        setShowVideoInterface(false);
      }
    }, 100);
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsStreaming(false);
    setShowVideoInterface(false);
  };

  // Capture photo
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to blob and create file
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `selfie-${Date.now()}.jpg`, { type: 'image/jpeg' });
        const imageUrl = URL.createObjectURL(blob);
        setCapturedImage(imageUrl);
        onCapture(file);
        setIsStreaming(false);
        setShowVideoInterface(false);
        stopCamera();
      }
    }, 'image/jpeg', 0.95);
  };

  // Retake photo
  const retakePhoto = () => {
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage);
    }
    setCapturedImage(null);
    startCamera();
  };

  // Handle file upload (fallback)
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCapturedImage(reader.result as string);
        onCapture(file);
      };
      reader.readAsDataURL(file);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
      if (capturedImage) {
        URL.revokeObjectURL(capturedImage);
      }
    };
  }, []);

  return (
    <div className={`selfie-capture ${className}`}>
      {/* Camera/Guide Overlay */}
      <div className="relative" style={{ width: `${width}px`, height: `${height}px` }}>
        {!capturedImage && !showVideoInterface && (
          <div className="absolute inset-0 bg-neutral-100 rounded-lg flex flex-col items-center justify-center border-2 border-dashed border-neutral-300">
            <Camera className="h-16 w-16 text-neutral-400 mb-4" />
            <p className="text-neutral-600 text-center px-4 mb-4">
              Take a selfie using your camera
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={startCamera}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
              >
                <Camera className="h-4 w-4" />
                Open Camera
              </button>
              <label className="px-4 py-2 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors flex items-center gap-2 cursor-pointer">
                <Upload className="h-4 w-4" />
                Upload File
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            </div>
            {error && (
              <div className="mt-4 px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm text-center">{error}</p>
                {permissionDenied && (
                  <p className="text-red-500 text-xs text-center mt-2">
                    Please allow camera access in your browser settings
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Video Feed */}
        {showVideoInterface && (
          <div className="relative">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover rounded-lg"
              style={{ width: `${width}px`, height: `${height}px` }}
            />

            {/* Face Guide Overlay - only show when streaming */}
            {isStreaming && (
              <div className="absolute inset-0 pointer-events-none">
                {/* Oval Face Guide */}
                <div
                  className="absolute border-4 border-primary-400 rounded-full opacity-70"
                  style={{
                    width: '50%',
                    height: '65%',
                    top: '15%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)'
                  }}
                />

                {/* Guide Text */}
                <div className="absolute bottom-8 left-0 right-0 text-center">
                  <p className="text-white text-lg font-semibold drop-shadow-lg">
                    Position your face within the oval
                  </p>
                  <p className="text-white text-sm drop-shadow-md mt-1">
                    Make sure your face is clearly visible
                  </p>
                </div>
              </div>
            )}

            {/* Capture Button */}
            {isStreaming && (
              <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
              <button
                type="button"
                onClick={capturePhoto}
                className="px-6 py-3 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors shadow-lg flex items-center gap-2"
              >
                <Camera className="h-5 w-5" />
                Capture
              </button>
              <button
                type="button"
                onClick={stopCamera}
                className="px-4 py-3 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors shadow-lg flex items-center gap-2"
              >
                <CameraOff className="h-5 w-5" />
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setFacingMode(facingMode === 'user' ? 'environment' : 'user');
                  stopCamera();
                  setTimeout(startCamera, 100);
                }}
                className="px-4 py-3 bg-neutral-600 text-white rounded-full hover:bg-neutral-700 transition-colors shadow-lg"
                title="Switch Camera"
              >
                <RefreshCw className="h-5 w-5" />
              </button>
            </div>
            )}
          </div>
        )}

        {/* Captured Image Preview */}
        {capturedImage && (
          <div className="relative">
            <img
              src={capturedImage}
              alt="Captured selfie"
              className="w-full h-full object-cover rounded-lg"
              style={{ width: `${width}px`, height: `${height}px` }}
            />

            {/* Success Indicator */}
            <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-2 shadow-lg">
              <Check className="h-4 w-4" />
            </div>

            {/* Action Buttons */}
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
              <button
                type="button"
                onClick={retakePhoto}
                className="px-6 py-3 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors shadow-lg flex items-center gap-2"
              >
                <RefreshCw className="h-5 w-5" />
                Retake
              </button>
              <label className="px-6 py-3 bg-neutral-600 text-white rounded-full hover:bg-neutral-700 transition-colors shadow-lg flex items-center gap-2 cursor-pointer">
                <Upload className="h-5 w-5" />
                Upload Different
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        )}

        {/* Hidden Canvas for Capture */}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
}
